'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { discardHTMLTags, getBaseImageUrl, getCurrencySymbol, getFinalDiscountTag } from '@/constants/hooks'
import { Offer } from '@/services/dataTypes'
import OfferDetailsToggle from './OfferDetailsToggle'
import OfferCTAButton from './OfferCTAButton'
import DiscountTag from './DiscountTag'
import ExpiryTag from './ExpiryTag'

interface Props {
    product: Offer
    domain: string
    merchantHref: string
    merchant_name: string
    merchant_logo: string
    pageType?: string
    productDetailUrl?: string | null
    modalType?: 'simple' | 'details'
    companyId?: string
}

function isValidImageUrl(src?: string | null): boolean {
    if (!src || typeof src !== 'string') return false
    const s = src.trim()
    if (!s || s === 'null' || s === 'undefined' || s === '/null' || s === 'none') return false
    if (s.includes('awltovhc.com') || s.includes('emjcd.com') || s.includes('anrdoezrs.net') || s.includes('linksynergy.com') || s.includes('clickserve.')) {
        return false
    }
    return true
}

const CouponCard = ({
    product,
    merchantHref,
    domain,
    merchant_name,
    merchant_logo,
    pageType,
    productDetailUrl,
    modalType = 'simple',
    companyId = ''
}: Props) => {
    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0
    const discountPercent = originalPrice > 0 && salePrice > 0 ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : null
    const finalDiscountTag = getFinalDiscountTag(product?.offer_title || product?.offer_detail, discountPercent)
    const isCoupon = !!product?.coupon_code
    const currencySymbol = getCurrencySymbol(product?.currency)

    // Check product image first (strictly excluding tracking pixels / banner_image)
    const rawProductImg =
        (isValidImageUrl(product?.product_image) ? product?.product_image : null) ||
        (isValidImageUrl((product as any)?.offer?.product_image) ? (product as any)?.offer?.product_image : null) ||
        (isValidImageUrl((product as any)?.image) ? (product as any)?.image : null) ||
        ''

    // Check store / merchant logo
    const rawMerchantLogo =
        (isValidImageUrl(merchant_logo) ? merchant_logo : null) ||
        (isValidImageUrl(product?.merchant?.merchant_logo) ? product?.merchant?.merchant_logo : null) ||
        (isValidImageUrl((product as any)?.merchant_logo) ? (product as any)?.merchant_logo : null) ||
        (isValidImageUrl((product as any)?.offer?.merchant?.merchant_logo) ? (product as any)?.offer?.merchant?.merchant_logo : null) ||
        ''

    const effectiveDomain = (!domain || domain === 'no domain in cookie' || domain.startsWith('localhost')) ? '' : domain

    const productImgUrl = rawProductImg ? getBaseImageUrl(effectiveDomain, rawProductImg, '') : ''
    const logoImgUrl = rawMerchantLogo ? getBaseImageUrl(effectiveDomain, rawMerchantLogo, '') : ''

    // Show product image if available, else show store logo (exact same pattern as Theme-5)
    const primaryImg = productImgUrl || logoImgUrl
    const [currentImg, setCurrentImg] = useState<string>(primaryImg)

    const handleImgError = () => {
        // If the product image failed, seamlessly fall back to the store logo
        if (currentImg === productImgUrl && logoImgUrl && logoImgUrl !== productImgUrl) {
            setCurrentImg(logoImgUrl)
        }
    }

    const displayName = merchant_name || product?.merchant?.merchant_name || 'Store'
    const displayTitle = product?.offer_title ? discardHTMLTags(product.offer_title) : displayName

    return (
        <div className="group relative bg-white rounded-2xl border border-slate-300 hover:border-[#00d69f]/40 flex flex-col h-full overflow-hidden transition-all duration-300 hover:shadow-[0_12px_30px_rgba(0,214,159,0.08)] hover:-translate-y-1 shadow-[0_2px_8px_rgba(0,0,0,0.02)] justify-between">
            {finalDiscountTag && <DiscountTag tag={finalDiscountTag} />}

            {/* Top Image Showcase: product image if available, falls back to store logo (same as Theme-5) */}
            <div className="relative bg-slate-50/60 h-28 sm:h-32 flex items-center justify-center overflow-hidden border-b border-slate-200 group-hover:bg-[#f8fafc]/50 transition-colors duration-300">
                {currentImg ? (
                    <Image
                        src={currentImg}
                        alt={displayTitle}
                        fill
                        unoptimized
                        sizes="(max-width: 640px) 100vw, 25vw"
                        onError={handleImgError}
                        className="object-contain p-2.5 group-hover:scale-105 transition-transform duration-500"
                    />
                ) : null}
            </div>

            <div className="p-3 flex-1 flex flex-col gap-1.5 relative z-10">
                {/* Offer Title */}
                <div className="relative min-h-[2.5rem] flex items-center">
                    {product?.is_detail === 1 && productDetailUrl ? (
                        <Link href={productDetailUrl}>
                            <h3 className="text-sm font-bold text-slate-900 leading-snug line-clamp-2 hover:text-[#00a87a] transition-colors">
                                {discardHTMLTags(product?.offer_title)}
                            </h3>
                        </Link>
                    ) : (
                        <h3 className="text-sm font-bold text-slate-900 leading-snug line-clamp-2 group-hover:text-[#00a87a] transition-colors">
                            {discardHTMLTags(product?.offer_title)}
                        </h3>
                    )}
                </div>

                {discardHTMLTags(product?.offer_detail) && (
                    <p className="text-xs text-slate-800 line-clamp-2 leading-relaxed min-h-[2rem] font-normal">
                        {discardHTMLTags(product?.offer_detail)}
                    </p>
                )}

                {/* Price display */}
                {originalPrice > 0 && salePrice > 0 && (
                    <div className="flex items-baseline gap-2 flex-wrap pt-0.5">
                        <span className="text-base font-extrabold text-slate-900">
                            {currencySymbol}{salePrice.toFixed(2)}
                        </span>
                        <span className="text-xs text-slate-400 line-through font-medium">
                            {currencySymbol}{originalPrice.toFixed(2)}
                        </span>
                    </div>
                )}

                {/* Expiry + View Details */}
                <div className="flex items-center justify-between gap-2 mt-auto pt-1.5 border-t border-slate-200">
                    <ExpiryTag endDate={product?.end_date} />
                    <OfferDetailsToggle
                        domain={domain}
                        imageSrc={currentImg || logoImgUrl}
                        merchantHref={merchantHref}
                        offer={product}
                        type="anchor"
                        merchantImg={rawMerchantLogo}
                        merchantName={displayName}
                        modalType={rawProductImg ? 'product' : modalType}
                        companyId={companyId}
                    />
                </div>
            </div>

            {/* CTA Footer */}
            <div className="px-3 pb-3 relative z-10">
                <OfferCTAButton
                    unique_id={product?.unique_id}
                    outUrl={product?.url}
                    merchantHref={merchantHref}
                    domain={domain}
                    isCoupon={isCoupon}
                    couponCode={product?.coupon_code}
                    label={product?.offer_type?.name === 'product' ? 'Buy Now' : (isCoupon ? 'Get Code' : 'Get Deal')}
                />
            </div>
        </div>
    )
}

export default CouponCard
